import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAllDataItems } from '../utils/dataItems';
import { searchDataItems } from '../utils/search';
import { secsData } from '../utils/secsDataLoader';
import DataItemCodesTable from '../components/DataItemCodesTable';

export default function DataItems() {
  const { name } = useParams<{ name?: string }>();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const allItems = useMemo(() => getAllDataItems(), []);
  const filteredItems = query.trim().length >= 2 ? searchDataItems(query) : allItems;

  const selected = useMemo(() => {
    if (name) {
      return allItems.find(d => d.name.toLowerCase() === name.toLowerCase()) ?? null;
    }
    return allItems[0] ?? null;
  }, [name, allItems]);

  const dataTypeName = (typeCode: string) => secsData.dataTypes[typeCode]?.name || typeCode;

  const handleSelect = (itemName: string) => {
    navigate(`/data-items/${itemName}`);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Data Item Dictionary</h1>
        <p className="text-[var(--color-text-secondary)]">
          Browse every SECS-II data item, its type, coded values, and where it is used.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        {/* Item list */}
        <div className="card p-4 h-fit">
          <div className="relative mb-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search data items..."
              className="input pl-10"
              aria-label="Search data items"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-text-tertiary)]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="text-xs text-[var(--color-text-tertiary)] mb-2">
            {filteredItems.length} of {allItems.length} items
          </div>
          <nav className="space-y-1 max-h-[60vh] overflow-y-auto scrollbar-thin" aria-label="Data item list">
            {filteredItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleSelect(item.name)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-mono transition-colors ${
                  selected?.name === item.name
                    ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100'
                    : 'hover:bg-[var(--color-bg-tertiary)] text-[var(--color-text-primary)]'
                }`}
              >
                {item.name}
              </button>
            ))}
            {filteredItems.length === 0 && (
              <p className="text-sm text-[var(--color-text-tertiary)] px-3 py-2">No matching data items.</p>
            )}
          </nav>
        </div>

        {/* Detail panel */}
        <div className="card p-6">
          {selected ? (
            <>
              <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-2xl font-bold font-mono text-primary-600 dark:text-primary-400">
                  {selected.name}
                </h2>
                <div className="flex items-center gap-2">
                  <span className="badge badge-primary font-mono">{selected.type}</span>
                  <span className="text-xs text-[var(--color-text-tertiary)]">{dataTypeName(selected.type)}</span>
                </div>
              </div>

              <div className="text-sm mb-4">
                <span className="text-xs uppercase text-[var(--color-text-tertiary)] mr-2">Length</span>
                <span className="font-mono">{selected.length || '-'}</span>
              </div>

              <p className="text-[var(--color-text-secondary)] leading-relaxed mb-6">
                {selected.description}
              </p>

              {selected.codes && selected.codes.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Coded Values</h3>
                  <DataItemCodesTable codes={selected.codes} />
                </div>
              )}

              {selected.notes && selected.notes.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Notes</h3>
                  <ul className="list-disc list-inside space-y-1 text-[var(--color-text-secondary)]">
                    {selected.notes.map((note, i) => (
                      <li key={i}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selected.usedIn && selected.usedIn.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-[var(--color-text-tertiary)] uppercase mb-2">
                    Used in {selected.usedIn.length} message{selected.usedIn.length > 1 ? 's' : ''}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selected.usedIn.map((msgId) => (
                      <button
                        key={msgId}
                        onClick={() => navigate(`/message/${msgId}`)}
                        className="text-xs px-2 py-1 bg-[var(--color-bg-tertiary)] hover:bg-primary-100 dark:hover:bg-primary-900 rounded font-mono transition-colors"
                      >
                        {msgId}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-[var(--color-text-secondary)]">
              {name ? `No data item named "${name}".` : 'Select a data item to view its details.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
